import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, ChevronDown, X } from "lucide-react";
import API from "../services/api";
import MarketSheet from "../components/MarketSheet";
import { getSuggestedSession } from "../utils/marketStatus";

const SUBMIT_SOUND = "/sounds/humordome-magic-button-click-453258.mp3";

const fallbackMarkets = [
  { id: 1, name: "SRIDEVI NIGHT" },
  { id: 2, name: "MADHUR NIGHT" },
  { id: 3, name: "MILAN NIGHT" },
  { id: 4, name: "ANMOL BAZAR" },
  { id: 5, name: "KALYAN NIGHT" },
  { id: 6, name: "RAJDHANI NIGHT" },
  { id: 7, name: "MAIN BAZAR" },
  { id: 8, name: "VANDE BHARAT" },
  { id: 9, name: "KALYAN" },
  { id: 10, name: "RAJDHANI DAY" },
  { id: 11, name: "MILAN DAY" },
  { id: 12, name: "SADAR BAZAR" },
  { id: 13, name: "MADHUR DAY" },
  { id: 14, name: "TIME BAZAR" },
  { id: 15, name: "SRIDEVI" },
  { id: 16, name: "MILAN MORNING" },
  { id: 17, name: "KANTARA" },
];

const digits = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

function SingleBet() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [markets, setMarkets] = useState([]);
  const [selectedMarket, setSelectedMarket] = useState(id || sessionStorage.getItem("selectedMarketId") || "1");
  const [session, setSession] = useState("CLOSE");
  const [amounts, setAmounts] = useState({});
  const [placing, setPlacing] = useState(false);
  const [limits, setLimits] = useState({ min_bet: "10", max_bet: "10000" });
  const [ankStatus, setAnkStatus] = useState({ ank_limit: null, used_digits: [], used_count: 0, remaining: null });
  const [marketSheetOpen, setMarketSheetOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const placingRef = useRef(false);

  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);

  useEffect(() => {
    if (!user?.id) navigate("/login", { replace: true });
  }, [navigate, user]);

  useEffect(() => {
    API.get("/markets")
      .then((res) => {
        const rows = Array.isArray(res.data) ? res.data : [];
        const normalRows = rows.filter((market) => (market.market_type || "normal") !== "starline");
        setMarkets(normalRows.length ? normalRows : fallbackMarkets);
      })
      .catch(() => setMarkets(fallbackMarkets));
    API.get("/settings/public")
      .then((res) => setLimits((current) => ({ ...current, ...(res.data || {}) })))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    if (!selectedMarket || !session) return;
    API.get(`/users/${user.id}/ank-status?market_id=${encodeURIComponent(selectedMarket)}&session=${encodeURIComponent(session)}`)
      .then((res) => setAnkStatus(res.data || { ank_limit: null, used_digits: [], used_count: 0, remaining: null }))
      .catch(() => {});
  }, [selectedMarket, session, user?.id]);

  const marketOptions = markets.length ? markets : fallbackMarkets;
  const selectedMarketInfo = marketOptions.find((market) => String(market.id) === String(selectedMarket)) || marketOptions[0];
  const total = Object.values(amounts).reduce((sum, value) => sum + Number(value || 0), 0);
  const confirmEntries = Object.entries(amounts).filter(([, value]) => Number(value) > 0);
  const today = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short" }).toUpperCase();

  useEffect(() => {
    if (!selectedMarketInfo) return;
    setSession(getSuggestedSession(selectedMarketInfo));
  }, [selectedMarketInfo]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isCompact = viewportWidth <= 430;
  const isTiny = viewportWidth <= 360;
  const isDesktop = viewportWidth >= 900;
  const playSubmitSound = () => {
    try {
      const audio = new Audio(SUBMIT_SOUND);
      audio.volume = 0.72;
      audio.currentTime = 0;
      audio.play().catch(() => {});
    } catch {}
  };
  const usedDigitSet = useMemo(() => new Set((ankStatus.used_digits || []).map((value) => String(value))), [ankStatus.used_digits]);
  const ankLimitReached = Number.isFinite(Number(ankStatus.ank_limit)) && Number(ankStatus.ank_limit) > 0 && Number(ankStatus.remaining || 0) <= 0;
  const getSelectedNewDigitCount = (nextAmounts = amounts) => {
    return Object.entries(nextAmounts)
      .filter(([, value]) => Number(value || 0) > 0)
      .map(([digit]) => String(digit))
      .filter((digit) => !usedDigitSet.has(digit)).length;
  };

  const isDigitDisabled = (digit) => {
    const key = String(digit);
    if (!ankLimitReached) return false;
    return !usedDigitSet.has(key) && !Number(amounts[digit] || 0);
  };

  const handleAmount = (digit, value) => {
    const cleanValue = String(value || "").replace(/[^\d]/g, "");
    const nextAmounts = { ...amounts, [digit]: cleanValue };
    if (Number(cleanValue || 0) <= 0) {
      setAmounts(nextAmounts);
      return;
    }

    const limit = Number(ankStatus.ank_limit || 0);
    const remaining = Number(ankStatus.remaining || 0);
    if (limit > 0 && !usedDigitSet.has(String(digit)) && getSelectedNewDigitCount(nextAmounts) > remaining) {
      alert(`You can select only ${ankStatus.ank_limit} ank in this market and session.`);
      return;
    }
    setAmounts(nextAmounts);
  };

  const handleMarketChange = (value) => {
    sessionStorage.setItem("selectedMarketId", String(value));
    setSelectedMarket(value);
  };

  const validateEntries = (entries) => {
    if (!entries.length) return "Enter amount for at least one number";
    const minBet = Number(limits.min_bet || 10);
    const maxBet = Number(limits.max_bet || 10000);
    const invalidEntry = entries.find(([, value]) => Number(value) < minBet || Number(value) > maxBet);
    if (invalidEntry) return `Each bet must be between Rs ${minBet} and Rs ${maxBet}`;
    const limit = Number(ankStatus.ank_limit || 0);
    const remaining = Number(ankStatus.remaining || 0);
    const newDigits = entries.map(([digit]) => String(digit)).filter((digit) => !usedDigitSet.has(digit));
    if (limit > 0 && new Set(newDigits).size > remaining) {
      return `Ank limit is ${limit} for this market and ${session} session. Remove extra ank before submit.`;
    }
    return "";
  };

  const openConfirm = () => {
    if (placingRef.current || placing) return;
    const message = validateEntries(confirmEntries);
    if (message) {
      alert(message);
      return;
    }
    setConfirmOpen(true);
  };

  const placeBet = async () => {
    if (placingRef.current || placing) return;
    if (!user?.id) {
      navigate("/login");
      return;
    }

    const message = validateEntries(confirmEntries);
    if (message) {
      alert(message);
      return;
    }

    const uniqueEntries = [];
    const seenDigits = new Set();
    for (const entry of confirmEntries) {
      const digit = String(entry[0]);
      if (seenDigits.has(digit)) continue;
      seenDigits.add(digit);
      uniqueEntries.push(entry);
    }

    placingRef.current = true;
    setPlacing(true);
    try {
      for (const [number, amount] of uniqueEntries) {
        await API.post("/place-bet", {
          user_id: user.id,
          market_id: selectedMarket,
          number,
          amount: Number(amount),
          type: `single-${session.toLowerCase()}`,
        });
      }
      setAmounts({});
      const statusRes = await API.get(`/users/${user.id}/ank-status?market_id=${encodeURIComponent(selectedMarket)}&session=${encodeURIComponent(session)}`);
      setAnkStatus(statusRes.data || { ank_limit: null, used_digits: [], used_count: 0, remaining: null });
      setConfirmOpen(false);
      playSubmitSound();
      alert("Bet placed successfully");
      navigate("/game-select");
    } catch (err) {
      alert(err?.response?.data?.message || "Error placing bet");
    } finally {
      placingRef.current = false;
      setPlacing(false);
    }
  };

  return (
    <div style={page}>
      <header style={{ ...topBar, ...(isDesktop ? topBarDesktop : null), padding: isCompact ? "5px 12px 0" : isDesktop ? topBarDesktop.padding : topBar.padding, gap: isCompact ? 10 : topBar.gap }}>
        <div style={headerLine} />
        <button type="button" style={circleBack} onClick={() => navigate(-1)}>
          <ArrowLeft size={22} strokeWidth={2.5} />
        </button>
        <h1 style={title}>SINGLE</h1>
        <div style={{ ...datePill, padding: isCompact ? "8px 10px" : datePill.padding, fontSize: isCompact ? 12 : datePill.fontSize }}>
          <CalendarDays size={16} />
          {today}
        </div>
      </header>

      <main style={{ ...main, ...(isDesktop ? mainDesktop : null), padding: isCompact ? "10px 10px 0" : isDesktop ? mainDesktop.padding : main.padding }}>
        <section style={{ ...selectorCard, ...(isDesktop ? selectorCardDesktop : null), gridTemplateColumns: isCompact ? "1fr" : selectorCard.gridTemplateColumns }}>
          <label style={fieldLabel}>
            Market <span style={required}>*</span>
            <span style={selectWrap}>
              <button type="button" style={{ ...select, ...selectButton }} onClick={() => setMarketSheetOpen(true)}>
                {selectedMarketInfo?.name || "Select Market"}
              </button>
              <ChevronDown size={18} style={selectIcon} />
            </span>
          </label>

          <label style={fieldLabel}>
            Session <span style={required}>*</span>
            <span style={selectWrap}>
              <select value={session} onChange={(event) => setSession(event.target.value)} style={select}>
                <option value="OPEN">OPEN</option>
                <option value="CLOSE">CLOSE</option>
              </select>
              <ChevronDown size={18} style={selectIcon} />
            </span>
          </label>
        </section>

        <section style={{ ...entryCard, ...(isDesktop ? entryCardDesktop : null), gridTemplateColumns: isDesktop ? "repeat(10, minmax(0, 1fr))" : isTiny ? "repeat(4, minmax(0, 1fr))" : entryCard.gridTemplateColumns, padding: isCompact ? 12 : isDesktop ? entryCardDesktop.padding : entryCard.padding, gap: isCompact ? 8 : isDesktop ? entryCardDesktop.gap : entryCard.gap }}>
          {Number.isFinite(Number(ankStatus.ank_limit)) && Number(ankStatus.ank_limit) > 0 && (
            <div style={ankBanner}>
              <strong>{`Ank Limit: ${ankStatus.used_count}/${ankStatus.ank_limit}`}</strong>
              <span>
                {ankLimitReached
                  ? "Ank limit reached for this market and session. Only already-used digits can be repeated."
                  : `${ankStatus.remaining} more digit${Number(ankStatus.remaining) === 1 ? "" : "s"} available in this market/session`}
              </span>
            </div>
          )}
          {digits.map((digit) => (
            <div key={digit} style={{ ...digitBlock, opacity: isDigitDisabled(digit) ? 0.45 : 1 }}>
              <div style={Number(amounts[digit] || 0) > 0 ? activeDigitBox : digitBox}>{digit}</div>
              <input
                style={amountInput}
                type="number"
                min="0"
                inputMode="numeric"
                placeholder="₹"
                value={amounts[digit] || ""}
                disabled={isDigitDisabled(digit)}
                onChange={(event) => handleAmount(digit, event.target.value)}
              />
            </div>
          ))}
        </section>
      </main>

      <footer style={{ ...footer, ...(isDesktop ? footerDesktop : null), gridTemplateColumns: isCompact ? "1fr" : footer.gridTemplateColumns, padding: isCompact ? "10px 12px calc(10px + env(safe-area-inset-bottom))" : isDesktop ? footerDesktop.padding : footer.padding }}>
        <div>
          <div style={totalLabel}>Total Amount</div>
          <div style={{ ...totalAmount, fontSize: isCompact ? 20 : totalAmount.fontSize }}>₹{total}</div>
        </div>
        <button
          type="button"
          disabled={!total || placing}
          style={{ ...placeButton, width: "100%", opacity: !total || placing ? 0.62 : 1 }}
          onClick={openConfirm}
        >
          {placing ? "Placing..." : "Place Bid"}
        </button>
      </footer>
      <MarketSheet
        open={marketSheetOpen}
        markets={marketOptions}
        selectedId={selectedMarket}
        onSelect={handleMarketChange}
        onClose={() => setMarketSheetOpen(false)}
      />
      {confirmOpen && (
        <div style={confirmOverlay} onClick={() => setConfirmOpen(false)}>
          <section style={{ ...confirmSheet, padding: isCompact ? "14px 14px 24px" : confirmSheet.padding }} onClick={(event) => event.stopPropagation()}>
            <div style={sheetHandle} />
            <div style={confirmHeader}>
              <h2 style={confirmTitle}>Confirm Your Bid</h2>
              <button type="button" style={closeButton} onClick={() => setConfirmOpen(false)}>
                <X size={20} />
              </button>
            </div>

            <div style={summaryBox}>
              <span style={summaryLabel}>Market & Session</span>
              <strong>{selectedMarketInfo?.name || "Market"} - {session}</strong>
            </div>

            <div style={numbersBox}>
              <span style={summaryLabel}>Numbers & Amounts</span>
              {confirmEntries.map(([number, amount]) => (
                <div key={number} style={confirmRow}>
                  <span>{number}</span>
                  <strong>Rs {Number(amount || 0).toFixed(0)}</strong>
                </div>
              ))}
            </div>

            <div style={totalBox}>
              <span>Total Amount</span>
              <strong>Rs {total.toFixed(0)}</strong>
            </div>

            <div style={{ ...confirmActions, gridTemplateColumns: isCompact ? "1fr" : confirmActions.gridTemplateColumns }}>
              <button type="button" style={cancelButton} onClick={() => setConfirmOpen(false)}>Cancel</button>
              <button type="button" style={confirmButton} disabled={placing} onClick={placeBet}>
                {placing ? "Placing..." : "Confirm Bid"}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

export default SingleBet;

const page = {
  minHeight: "100vh",
  background: "#f9fafb",
  color: "#111827",
  fontFamily: "Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  paddingTop: 58,
  paddingBottom: 132,
  boxSizing: "border-box",
};

const topBar = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  zIndex: 50,
  height: 58,
  background: "linear-gradient(180deg, #fffdf8 0%, #fff7ed 100%)",
  display: "grid",
  gridTemplateColumns: "38px 1fr auto",
  alignItems: "center",
  gap: 10,
  padding: "4px 10px 0",
  borderBottom: "1px solid #fde7c2",
  boxShadow: "0 4px 14px rgba(146, 64, 14, 0.08)",
  boxSizing: "border-box",
};

const topBarDesktop = {
  maxWidth: "100%",
  height: 72,
  padding: "6px max(24px, calc((100vw - 1440px) / 2)) 0",
  gridTemplateColumns: "48px 1fr auto",
};

const headerLine = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: 4,
  background: "linear-gradient(90deg, #f59e0b, #dc2626, #f97316)",
};

const circleBack = {
  width: 38,
  height: 38,
  borderRadius: "50%",
  border: "1px solid #e5e7eb",
  background: "linear-gradient(135deg, #fff7ed, #fffbeb)",
  color: "#92400e",
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
};

const title = {
  margin: 0,
  fontSize: 14,
  fontWeight: 800,
  letterSpacing: "0.025em",
  color: "#92400e",
  textTransform: "uppercase",
};

const datePill = {
  minWidth: 0,
  border: "1px solid #fcd34d",
  background: "linear-gradient(90deg, #fffbeb, #fff7ed)",
  color: "#b45309",
  borderRadius: 999,
  padding: "6px 10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.025em",
};

const main = {
  width: "100%",
  maxWidth: 448,
  margin: "0 auto",
  padding: "10px 10px 0",
  boxSizing: "border-box",
};

const mainDesktop = {
  maxWidth: 1440,
  padding: "24px 24px 0",
};

const selectorCard = {
  background: "linear-gradient(135deg, #fff7ed, #fffbeb)",
  border: "1px solid #fde68a",
  borderRadius: 10,
  padding: 10,
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 8,
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
  boxSizing: "border-box",
};

const selectorCardDesktop = {
  borderRadius: 18,
  padding: 18,
  gap: 16,
};

const fieldLabel = {
  color: "#92400e",
  fontSize: 10,
  fontWeight: 700,
};

const required = {
  color: "#ef4444",
};

const selectWrap = {
  position: "relative",
  display: "block",
  marginTop: 8,
};

const select = {
  width: "100%",
  height: 40,
  appearance: "none",
  border: "2px solid #fcd34d",
  borderRadius: 10,
  background: "#fff",
  color: "#1f2937",
  padding: "0 30px 0 10px",
  fontSize: 12,
  fontWeight: 600,
  outline: "none",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
};

const selectButton = {
  display: "block",
  textAlign: "left",
  lineHeight: "48px",
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  cursor: "pointer",
};

const selectIcon = {
  position: "absolute",
  right: 8,
  top: 11,
  color: "#d97706",
  pointerEvents: "none",
};

const entryCard = {
  marginTop: 10,
  background: "linear-gradient(135deg, #fff7ed, #fffbeb)",
  border: "1px solid #fde68a",
  borderRadius: 10,
  padding: 10,
  display: "grid",
  gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
  gap: 8,
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
  boxSizing: "border-box",
};

const entryCardDesktop = {
  padding: 18,
  gap: 14,
  borderRadius: 20,
};

const ankBanner = {
  gridColumn: "1 / -1",
  display: "grid",
  gap: 4,
  padding: "8px 10px",
  borderRadius: 12,
  border: "1px solid #fcd34d",
  background: "#fff7ed",
  color: "#b45309",
  fontSize: 11,
  fontWeight: 700,
};

const digitBlock = {
  display: "grid",
  gap: 6,
};

const digitBox = {
  height: 32,
  borderRadius: 8,
  background: "#fdecc8",
  border: "1px solid #fcd34d",
  display: "grid",
  placeItems: "center",
  color: "#92400e",
  fontSize: 12,
  fontWeight: 600,
};

const activeDigitBox = {
  ...digitBox,
  background: "#d97706",
  borderColor: "#d97706",
  color: "#fff",
  boxShadow: "0 8px 18px rgba(217,119,6,0.24)",
};

const amountInput = {
  height: 36,
  minWidth: 0,
  borderRadius: 12,
  border: "2px solid #d1d5db",
  background: "#fff",
  color: "#111827",
  textAlign: "center",
  fontSize: 12,
  fontWeight: 600,
  outline: "none",
  padding: "0 4px",
  boxSizing: "border-box",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
};

const footer = {
  position: "fixed",
  left: 0,
  right: 0,
  bottom: 0,
  background: "#fff",
  minHeight: 62,
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  alignItems: "center",
  gap: 10,
  padding: "8px max(10px, calc((100vw - 448px) / 2))",
  borderTop: "2px solid #e5e7eb",
  boxShadow: "0 -12px 24px rgba(15, 23, 42, 0.12)",
  boxSizing: "border-box",
};

const footerDesktop = {
  gridTemplateColumns: "1fr minmax(220px, 320px)",
  padding: "10px max(24px, calc((100vw - 1440px) / 2))",
};

const totalLabel = {
  color: "#6b7280",
  fontSize: 10,
  fontWeight: 400,
};

const totalAmount = {
  color: "#d97706",
  fontSize: 17,
  fontWeight: 900,
  marginTop: 2,
};

const placeButton = {
  height: 46,
  border: 0,
  borderRadius: 12,
  background: "linear-gradient(135deg, #22c55e, #16a34a)",
  color: "#fff",
  fontSize: 13,
  fontWeight: 800,
  cursor: "pointer",
  boxShadow: "0 12px 22px rgba(34,197,94,0.22)",
};

const confirmOverlay = {
  position: "fixed",
  inset: 0,
  zIndex: 100,
  background: "rgba(15,23,42,0.58)",
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "center",
};
const confirmSheet = {
  width: "min(428px, 100%)",
  background: "#fff",
  borderRadius: "24px 24px 0 0",
  padding: "14px 20px 28px",
  boxSizing: "border-box",
  display: "grid",
  gap: 14,
};
const sheetHandle = {
  width: 44,
  height: 5,
  borderRadius: 999,
  background: "#cbd5e1",
  justifySelf: "center",
};
const confirmHeader = { display: "flex", alignItems: "center", justifyContent: "space-between" };
const confirmTitle = { margin: 0, fontSize: 20, fontWeight: 900, color: "#111827" };
const closeButton = { width: 38, height: 38, border: 0, borderRadius: "50%", background: "#f1f5f9", color: "#64748b", display: "grid", placeItems: "center" };
const summaryBox = { background: "#eff6ff", borderRadius: 12, padding: "14px", display: "grid", gap: 8, color: "#1e293b" };
const summaryLabel = { textTransform: "uppercase", color: "#4f7ee8", fontSize: 11, fontWeight: 900, letterSpacing: "0.04em" };
const numbersBox = { background: "#f8fafc", borderRadius: 12, padding: "14px", display: "grid", gap: 8 };
const confirmRow = { display: "flex", justifyContent: "space-between", alignItems: "center", color: "#334155", fontSize: 16 };
const totalBox = { minHeight: 62, borderRadius: 12, padding: "0 16px", background: "#ecfdf5", color: "#334155", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 16 };
const confirmActions = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 };
const cancelButton = { height: 54, border: 0, borderRadius: 12, background: "#eef2f7", color: "#334155", fontWeight: 800, fontSize: 15 };
const confirmButton = { height: 54, border: 0, borderRadius: 12, background: "#315ee8", color: "#fff", fontWeight: 900, fontSize: 15 };

